import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Category as CategoryIcon,
  Edit as EditIcon,
  KeyboardArrowRight as ChevronIcon,
  SubdirectoryArrowRight as SubIcon,
  ToggleOn as ActiveIcon,
  ToggleOff as InactiveIcon,
} from '@mui/icons-material';
import { useVenue } from '../../../../context/VenueContext';
import PageHeader from '../../../../components/venue/PageHeader';
import EmptyState from '../../../../components/venue/EmptyState';
import inventoryCategoryService, { extractCategoryError } from '../../../../services/inventoryCategoryService';
import type { InventoryCategory, InventorySubcategory } from '../../../../types/venue';

// ── Category edit dialog state ──────────────────────────────────────────────────

interface CategoryDialogState {
  mode: 'create' | 'edit';
  category?: InventoryCategory;
}

interface SubcategoryDialogState {
  mode: 'create' | 'edit';
  categoryId: string;
  subcategory?: InventorySubcategory;
}

// ── Shared row bits ─────────────────────────────────────────────────────────────

/** Quiet status chip: Active reads muted; Inactive is a soft warning outline. */
const StatusChip: React.FC<{ active: boolean }> = ({ active }) => (
  <Chip
    label={active ? 'Active' : 'Inactive'}
    size="small"
    variant="outlined"
    color={active ? 'default' : 'warning'}
    sx={{ fontSize: '0.66rem', height: 20, fontWeight: 500 }}
  />
);

/** Right-aligned edit + activate/deactivate actions (no loud inline toggles). */
const RowActions: React.FC<{
  active: boolean;
  entity: 'category' | 'subcategory';
  onEdit: () => void;
  onToggle: () => void;
}> = ({ active, entity, onEdit, onToggle }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
    <Tooltip title={`Edit ${entity}`}>
      <IconButton size="small" onClick={onEdit}>
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title={active ? `Deactivate ${entity}` : `Activate ${entity}`}>
      <IconButton size="small" onClick={onToggle}>
        {active
          ? <ActiveIcon fontSize="small" color="success" />
          : <InactiveIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
      </IconButton>
    </Tooltip>
  </Box>
);

const InventoryCategoryManagement: React.FC = () => {
  const theme = useTheme();
  const { venueId } = useVenue();

  const backPath = `/admin/venues/${venueId}/inventory`;

  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Expanded rows → loaded subcategories
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [subsByCategory, setSubsByCategory] = useState<Record<string, InventorySubcategory[]>>({});
  const [subsLoading, setSubsLoading] = useState<Record<string, boolean>>({});

  // Dialogs
  const [catDialog, setCatDialog] = useState<CategoryDialogState | null>(null);
  const [catName, setCatName] = useState('');
  const [catActive, setCatActive] = useState(true);
  const [catSaving, setCatSaving] = useState(false);
  const [catError, setCatError] = useState<string | null>(null);

  const [subDialog, setSubDialog] = useState<SubcategoryDialogState | null>(null);
  const [subName, setSubName] = useState('');
  const [subActive, setSubActive] = useState(true);
  const [subSaving, setSubSaving] = useState(false);
  const [subError, setSubError] = useState<string | null>(null);

  // ── Load categories ─────────────────────────────────────────────────────────
  const loadCategories = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      // withSubcategories nests active subcategories so we can show an at-rest
      // count without a fetch per row. The expanded view lazy-loads the full
      // set (including inactive) when a row is opened.
      const result = await inventoryCategoryService.getCategories(venueId, {
        includeInactive,
        withSubcategories: true,
      });
      setCategories(result);
    } catch (err) {
      setError(extractCategoryError(err));
    } finally {
      setLoading(false);
    }
  }, [venueId, includeInactive]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  // ── Subcategories ─────────────────────────────────────────────────────────────
  const loadSubcategories = useCallback(async (categoryId: string) => {
    if (!venueId) return;
    setSubsLoading(prev => ({ ...prev, [categoryId]: true }));
    try {
      const subs = await inventoryCategoryService.getSubcategories(venueId, categoryId, { includeInactive: true });
      setSubsByCategory(prev => ({ ...prev, [categoryId]: subs }));
    } catch {
      setSubsByCategory(prev => ({ ...prev, [categoryId]: [] }));
    } finally {
      setSubsLoading(prev => ({ ...prev, [categoryId]: false }));
    }
  }, [venueId]);

  const toggleExpand = (categoryId: string) => {
    const next = !expanded[categoryId];
    setExpanded(prev => ({ ...prev, [categoryId]: next }));
    if (next && !subsByCategory[categoryId]) loadSubcategories(categoryId);
  };

  // ── Category CRUD ───────────────────────────────────────────────────────────
  const openCreateCategory = () => {
    setCatDialog({ mode: 'create' });
    setCatName('');
    setCatActive(true);
    setCatError(null);
  };

  const openEditCategory = (category: InventoryCategory) => {
    setCatDialog({ mode: 'edit', category });
    setCatName(category.name);
    setCatActive(category.active);
    setCatError(null);
  };

  const handleSaveCategory = async () => {
    if (!venueId || !catDialog || !catName.trim()) return;
    setCatSaving(true);
    setCatError(null);
    try {
      if (catDialog.mode === 'create') {
        await inventoryCategoryService.createCategory(venueId, { name: catName.trim(), active: catActive });
      } else if (catDialog.category) {
        await inventoryCategoryService.updateCategory(venueId, catDialog.category._id, {
          name: catName.trim(),
          active: catActive,
        });
      }
      setCatDialog(null);
      loadCategories();
    } catch (err) {
      setCatError(extractCategoryError(err));
    } finally {
      setCatSaving(false);
    }
  };

  const handleToggleCategoryActive = async (category: InventoryCategory) => {
    if (!venueId) return;
    try {
      await inventoryCategoryService.updateCategory(venueId, category._id, { active: !category.active });
      loadCategories();
    } catch (err) {
      setError(extractCategoryError(err));
    }
  };

  // ── Subcategory CRUD ────────────────────────────────────────────────────────
  const openCreateSubcategory = (categoryId: string) => {
    setSubDialog({ mode: 'create', categoryId });
    setSubName('');
    setSubActive(true);
    setSubError(null);
  };

  const openEditSubcategory = (categoryId: string, subcategory: InventorySubcategory) => {
    setSubDialog({ mode: 'edit', categoryId, subcategory });
    setSubName(subcategory.name);
    setSubActive(subcategory.active);
    setSubError(null);
  };

  const handleSaveSubcategory = async () => {
    if (!venueId || !subDialog || !subName.trim()) return;
    setSubSaving(true);
    setSubError(null);
    try {
      if (subDialog.mode === 'create') {
        await inventoryCategoryService.createSubcategory(venueId, subDialog.categoryId, {
          name: subName.trim(),
          active: subActive,
        });
      } else if (subDialog.subcategory) {
        await inventoryCategoryService.updateSubcategory(venueId, subDialog.subcategory._id, {
          name: subName.trim(),
          active: subActive,
        });
      }
      const cid = subDialog.categoryId;
      setSubDialog(null);
      loadSubcategories(cid);
    } catch (err) {
      setSubError(extractCategoryError(err));
    } finally {
      setSubSaving(false);
    }
  };

  const handleToggleSubcategoryActive = async (categoryId: string, subcategory: InventorySubcategory) => {
    if (!venueId) return;
    try {
      await inventoryCategoryService.updateSubcategory(venueId, subcategory._id, { active: !subcategory.active });
      loadSubcategories(categoryId);
    } catch (err) {
      setError(extractCategoryError(err));
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        backLink={{ label: 'Inventory', to: backPath }}
        icon={<CategoryIcon />}
        title="Categories"
        subtitle="Categories and subcategories are venue-defined labels for classifying inventory items. Deactivate a category to hide it from item pickers without losing history."
        actions={
          <>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={includeInactive}
                  onChange={e => setIncludeInactive(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Show inactive</Typography>}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateCategory}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Add Category
            </Button>
          </>
        }
      />

      {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  { label: 'Category', align: 'left' as const },
                  { label: 'Subcategories', align: 'right' as const },
                  { label: 'Status', align: 'left' as const },
                  { label: 'Actions', align: 'right' as const },
                ].map(col => (
                  <TableCell key={col.label} align={col.align}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={26} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <EmptyState icon={CategoryIcon} message="No categories yet. Use Add Category to create one." />
                  </TableCell>
                </TableRow>
              ) : (
                categories.map(cat => {
                  const isOpen = !!expanded[cat._id];
                  // Before expand we fall back to the nested (active) subcategories
                  // from withSubcategories; once expanded we use the lazily-loaded
                  // full set. Either way we filter by the Show-inactive toggle.
                  const availableSubs = subsByCategory[cat._id] ?? cat.subcategories ?? [];
                  const shownSubs = availableSubs.filter(s => includeInactive || s.active);
                  return (
                    <React.Fragment key={cat._id}>
                      {/* ── Category row ─────────────────────────────────────── */}
                      <TableRow hover sx={{ '& td': { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}` } }}>
                        <TableCell>
                          <Box
                            role="button"
                            onClick={() => toggleExpand(cat._id)}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 0.5,
                              cursor: 'pointer', userSelect: 'none',
                            }}
                          >
                            <ChevronIcon
                              fontSize="small"
                              sx={{
                                color: theme.palette.text.secondary,
                                transform: isOpen ? 'rotate(90deg)' : 'none',
                                transition: 'transform 0.2s',
                              }}
                            />
                            <Typography variant="body2" fontWeight={600}>{cat.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">{shownSubs.length}</Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip active={cat.active} />
                        </TableCell>
                        <TableCell align="right">
                          <RowActions
                            active={cat.active}
                            entity="category"
                            onEdit={() => openEditCategory(cat)}
                            onToggle={() => handleToggleCategoryActive(cat)}
                          />
                        </TableCell>
                      </TableRow>

                      {/* ── Subcategory child rows (only when expanded) ──────── */}
                      {isOpen && (
                        subsLoading[cat._id] ? (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ py: 1.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress size={18} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : (
                          <>
                            {shownSubs.map(sub => (
                              <TableRow
                                key={sub._id}
                                sx={{
                                  backgroundColor: alpha(theme.palette.action.hover, 0.18),
                                  '& td': { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`, py: 0.5 },
                                }}
                              >
                                <TableCell sx={{ pl: 4 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SubIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
                                    <Typography variant="body2">{sub.name}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="right" />
                                <TableCell>
                                  <StatusChip active={sub.active} />
                                </TableCell>
                                <TableCell align="right">
                                  <RowActions
                                    active={sub.active}
                                    entity="subcategory"
                                    onEdit={() => openEditSubcategory(cat._id, sub)}
                                    onToggle={() => handleToggleSubcategoryActive(cat._id, sub)}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                            {shownSubs.length === 0 && (
                              <TableRow sx={{ backgroundColor: alpha(theme.palette.action.hover, 0.18) }}>
                                <TableCell colSpan={4} sx={{ pl: 4, py: 1, border: 'none' }}>
                                  <Typography variant="body2" color="text.disabled">
                                    No subcategories yet.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            <TableRow sx={{ backgroundColor: alpha(theme.palette.action.hover, 0.18) }}>
                              <TableCell colSpan={4} sx={{ pl: 4, py: 0.5 }}>
                                <Button
                                  size="small"
                                  startIcon={<AddIcon fontSize="small" />}
                                  onClick={() => openCreateSubcategory(cat._id)}
                                  sx={{ textTransform: 'none', fontWeight: 500 }}
                                >
                                  Add subcategory
                                </Button>
                              </TableCell>
                            </TableRow>
                          </>
                        )
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Category dialog */}
      <Dialog open={!!catDialog} onClose={() => setCatDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{catDialog?.mode === 'create' ? 'Add Category' : 'Edit Category'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          {catError && <Alert severity="error">{catError}</Alert>}
          <TextField
            label="Name"
            size="small"
            fullWidth
            autoFocus
            value={catName}
            onChange={e => setCatName(e.target.value)}
            inputProps={{ maxLength: 120 }}
          />
          <FormControlLabel
            control={<Checkbox checked={catActive} onChange={e => setCatActive(e.target.checked)} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatDialog(null)} sx={{ textTransform: 'none' }} disabled={catSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCategory}
            disabled={catSaving || !catName.trim()}
            startIcon={catSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {catSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subcategory dialog */}
      <Dialog open={!!subDialog} onClose={() => setSubDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{subDialog?.mode === 'create' ? 'Add Subcategory' : 'Edit Subcategory'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          {subError && <Alert severity="error">{subError}</Alert>}
          <TextField
            label="Name"
            size="small"
            fullWidth
            autoFocus
            value={subName}
            onChange={e => setSubName(e.target.value)}
            inputProps={{ maxLength: 120 }}
          />
          <FormControlLabel
            control={<Checkbox checked={subActive} onChange={e => setSubActive(e.target.checked)} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubDialog(null)} sx={{ textTransform: 'none' }} disabled={subSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSubcategory}
            disabled={subSaving || !subName.trim()}
            startIcon={subSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {subSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryCategoryManagement;
